import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * Catches a render-time crash in a screen and shows it, rather than letting React unmount the
 * whole tree.
 *
 * Without one of these, a single bad component takes the entire application down to a blank
 * page — no message, no navigation, nothing to act on. That failure mode is worse than the
 * bug that caused it, because it destroys the evidence: the user cannot say what they were
 * looking at, and the URL is often all that survives.
 *
 * Resets on navigation, so moving to another screen recovers without a page reload.
 */
export class ErrorBoundary extends Component<
  { children: ReactNode; resetKey?: string },
  { error: Error | null; where: string | null }
> {
  override state: { error: Error | null; where: string | null } = {
    error: null,
    where: null,
  };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ClientMa] A screen failed to render.', error, info.componentStack);

    // Kept, not only logged. "Cannot read properties of undefined" names no component and is
    // the same sentence for every one of them — so a report that carries only the message
    // costs an afternoon of reading files, which is exactly what this boundary was built to
    // avoid. The stack goes on the screen the person is already looking at.
    this.setState({ where: info.componentStack ?? null });
  }

  override componentDidUpdate(previous: { children: ReactNode; resetKey?: string }) {
    // Navigating away clears the error, so a broken screen does not poison the whole session.
    if (this.state.error && previous.resetKey !== this.props.resetKey) {
      this.setState({ error: null, where: null });
    }
  }

  override render() {
    const { error, where } = this.state;

    if (!error) return this.props.children;

    // The nearest few components, innermost first. The whole stack reaches the application
    // root and the frames above the screen are the same on every failure, so they say nothing.
    const frames = (where ?? '')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .slice(0, 6);

    return (
      <div className="cma-page">
        <div className="cma-card" style={{ padding: 'var(--space-8)' }}>
          <h2 className="cma-card__title">This screen could not be displayed</h2>

          <p style={{ marginTop: 'var(--space-2)', color: 'var(--text-muted)' }}>
            Something in this page failed while rendering. The rest of the application is
            unaffected — use the navigation to continue, or reload to try again.
          </p>

          {/* The message is shown because a blank card teaches the user nothing and gives
              support nothing to work from. */}
          <pre
            style={{
              marginTop: 'var(--space-4)',
              padding: 'var(--space-3)',
              background: 'var(--ink-50)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius)',
              fontSize: 'var(--text-xs)',
              color: 'var(--text-secondary)',
              whiteSpace: 'pre-wrap',
              overflowX: 'auto',
            }}
          >
            {error.message}
            {frames.length > 0 && `\n\n${frames.join('\n')}`}
          </pre>

          <div className="cma-row" style={{ marginTop: 'var(--space-4)' }}>
            <button
              className="cma-btn cma-btn--primary"
              onClick={() => this.setState({ error: null, where: null })}
            >
              Try again
            </button>
            <button className="cma-btn cma-btn--secondary" onClick={() => window.location.reload()}>
              Reload the page
            </button>
          </div>
        </div>
      </div>
    );
  }
}
